import * as React from 'react';

const FAST_REFRESH_MS = 5000;
const NORMAL_REFRESH_MS = 30000;
const BACKGROUND_REFRESH_MS = 5 * 60 * 1000;

type PipelineRun = {
  state?: string;
};

type PipelineState = {
  latest_timestamp?: number | string | null;
  runs?: Record<string, PipelineRun>;
};

const isTerminalPipelineState = (state?: string | null) => {
  return (
    state !== 'processing' &&
    state !== 'initialising' &&
    state !== 'cancelling'
  );
};

export function usePipelineAutoRefresh<T>({
  items,
  disabled = false,
  isPageActive = true,
  onRefresh,
  getPipelineState,
  fastRefreshMs = FAST_REFRESH_MS,
  normalRefreshMs = NORMAL_REFRESH_MS,
  backgroundRefreshMs = BACKGROUND_REFRESH_MS
}: {
  items: T[];
  disabled?: boolean;
  isPageActive?: boolean;
  onRefresh: () => void | Promise<void>;
  getPipelineState: (item: T) => PipelineState | null | undefined;
  fastRefreshMs?: number;
  normalRefreshMs?: number;
  backgroundRefreshMs?: number;
}) {
  const [isTabVisible, setIsTabVisible] = React.useState(() => {
    if (typeof document === 'undefined') return true;
    return document.visibilityState === 'visible';
  });

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabVisible(document.visibilityState === 'visible');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const hasInFlightItems = React.useMemo(() => {
    return items.some((item) => {
      const pipelineState = getPipelineState(item);

      if (!pipelineState) return false;

      const latestTimestamp = pipelineState.latest_timestamp ?? -1;

      if (Number(latestTimestamp) < 0) return false;

      if (Number(latestTimestamp) === 0 && pipelineState.runs) {
        return true;
      }

      const pRun = pipelineState.runs?.[String(latestTimestamp)];

      if (!pRun?.state) return false;

      return !isTerminalPipelineState(pRun.state);
    });
  }, [items, getPipelineState]);

  React.useEffect(() => {
    if (disabled) return;
    if (!isTabVisible) return;

    const intervalMs = isPageActive
      ? hasInFlightItems
        ? fastRefreshMs
        : normalRefreshMs
      : backgroundRefreshMs;

    const interval = window.setInterval(() => {
      void onRefresh();
    }, intervalMs);

    return () => {
      window.clearInterval(interval);
    };
  }, [
    disabled,
    isTabVisible,
    isPageActive,
    hasInFlightItems,
    onRefresh,
    fastRefreshMs,
    normalRefreshMs,
    backgroundRefreshMs
  ]);

  return {
    hasInFlightItems,
    isTabVisible
  };
}
/*import * as React from 'react';

const FAST_REFRESH_MS = 5000;
const NORMAL_REFRESH_MS = 30000;
const BACKGROUND_REFRESH_MS = 5 * 60 * 1000;

type PipelineRun = {
  state?: string;
};

type PipelineState = {
  latest_timestamp?: number | string | null;
  runs?: Record<string, PipelineRun>;
};

type PipelineItem = {
  pipelineState?: PipelineState | null;
};

const isTerminalPipelineState = (state?: string | null) => {
  return (
    state !== 'processing' &&
    state !== 'initialising' &&
    state !== 'cancelling'
  );
};

const hasInFlightPipelineItems = <T extends PipelineItem>(items: T[]) => {
  return items.some((item) => {
    const pipelineState = item.pipelineState;

    if (!pipelineState) return false;

    const latestTimestamp = pipelineState.latest_timestamp ?? -1;

    if (Number(latestTimestamp) < 0) return false;

    if (Number(latestTimestamp) === 0 && pipelineState.runs) {
      return true;
    }

    const pRun = pipelineState.runs?.[String(latestTimestamp)];

    if (!pRun?.state) return false;

    return !isTerminalPipelineState(pRun.state);
  });
};

export function usePipelineAutoRefresh<T extends PipelineItem>({
  items,
  disabled = false,
  onRefresh,
  fastRefreshMs = FAST_REFRESH_MS,
  normalRefreshMs = NORMAL_REFRESH_MS
}: {
  items: T[];
  disabled?: boolean;
  onRefresh: () => void | Promise<void>;
  fastRefreshMs?: number;
  normalRefreshMs?: number;
}) {
  const hasInFlightItems = React.useMemo(() => {
    return hasInFlightPipelineItems(items);
  }, [items]);

  React.useEffect(() => {
    if (disabled) return;

    const intervalMs = hasInFlightItems ? fastRefreshMs : normalRefreshMs;

    const interval = window.setInterval(() => {
      void onRefresh();
    }, intervalMs);

    return () => {
      window.clearInterval(interval);
    };
  }, [disabled, hasInFlightItems, onRefresh, fastRefreshMs, normalRefreshMs]);

  return {
    hasInFlightItems
  };
}*/